import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import FinancialAidViewer from "../components/FinancialAidViewer";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import PaginationFooter from "../components/PaginationFooter";
import AppTooltip from "../components/AppTooltip";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as financialAidApi from "../api/financialAid";
import * as registrationsApi from "../api/registrations";
import * as coursesApi from "../api/courses";
import * as tempAidApi from "../api/tempAid";

export default function AdminUserTable() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_financial_aid");
  const { hasPermission: canEdit } = useViewPermission("edit_financial_aid");
  const { busy: mutating, run: runMutation } = useMutationLock();

  const tableKeys = [
    "email",
    "userId",
    "studentName",
    "studentCnic",
    "studentPhoneNumber",
    "studentAddress",
    "fatherName",
    "fatherCnic",
    "fatherPhoneNumber",
    "fatherMaritalStatus",
    "fatherAddress",
    "fatherEmploymentStatus",
    "fatherOccupation",
    "motherName",
    "motherCnic",
    "motherPhoneNumber",
    "motherMaritalStatus",
    "motherAddress",
    "motherEmploymentStatus",
    "livingArrangement",
    "houseSize",
    "noOfSibling",
    "monthlySalary",
    "agriculturalLand",
    "additionalAssets",
    "schoolFees",
    "tutionFees",
    "transportCost",
    "groceryExpense",
    "electricityBill",
    "availingLoans",
    "loanAmount",
    "repayTerms",
    "ownAccount",
    "statementOfPurpose",
    "cnicPicture",
    "fatherCnicPicture",
    "motherCnicPicture",
    "schoolFeesPicture",
    "electricityBillPicture",
    "guardianBankStatement",
    "bankStatement",
  ];

  const parseCourseFee = (fee) => {
    const parsed = parseInt(String(fee || "").replace(/[^0-9]/g, ""), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const parseCourseDiscount = (discount) => {
    const parsed = parseFloat(String(discount || "0").replace(/[^0-9.]/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const presetDiscountOptions = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  const handleEdit = (field, value) => {
    const updatedFinancialAids = { ...financialAidData };
    updatedFinancialAids[field] = value;
    setFinancialAidData(updatedFinancialAids);
  };

  const handleSave = async (id, field, value) => {
    if (!canEdit) return;
    await runMutation(async () => {
      try {
        await financialAidApi.update(id, { field, value });
        notificationCtx.show({
          message: "Financial aid field updated successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error updating financial aid field:", error);
        notificationCtx.show({
          message: "Failed to update financial aid field.",
          stat: "error",
        });
      }
    });
  };

  const handleCommentEdit = async (id, field, value) => {
    if (!canEdit) return;
    await runMutation(async () => {
      try {
        await registrationsApi.update(id, { field, value });
        notificationCtx.show({
          message: "Registration updated successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error updating registration:", error);
        notificationCtx.show({
          message: "Failed to update the registration.",
          stat: "error",
        });
      }
    });
  };

  const [data, setdata] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = DEFAULT_PAGE_SIZE;
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [emailFilter, setEmailFilter] = useState("");
  const debouncedEmail = useDebouncedValue(emailFilter, 400);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRegistrations = async (pageArg = 1) => {
    try {
      if (!initialLoading) setRefreshing(true);
      const response = await registrationsApi.list({
        page: pageArg,
        limit,
        search: debouncedEmail,
        filters: { feeMode: "partial" },
      });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setdata(normalized.data);
      setCurrentPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (error) {
      console.error(
        "Error:",
        error.response ? error.response.data : error.message,
      );
      setdata([]);
      setTotal(0);
      setTotalPages(0);
      notificationCtx.show({
        message: "Failed to fetch registrations.",
        stat: "error",
      });
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!hasPermission) return;
    setCurrentPage(1);
    fetchRegistrations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, debouncedEmail]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchRegistrations(newPage);
  };

  const handleEmailFilterChange = (e) => {
    setEmailFilter(e.target.value);
    setCurrentPage(1);
  };

  const [selectedComment, setSelectedComment] = useState(null);
  const [editingComment, setEditingComment] = useState(false);
  const [commentValue, setCommentValue] = useState("");

  const columnKeys = [
    // "registrationId",
    "userInfo",
    // "name",
    // "email",
    // "phone",
    // "city",
    "courses",
    "fee",
    // "view form",
    // "return for edit",
    // "reject request",
    "form",
    "courseDiscounts",
    "discountedfee",
    "comments",
  ];

  const [financialAidData, setFinancialAidData] = useState(null);
  const [showFinancialAidModal, setShowFinancialAidModal] = useState(false);

  const viewFinancialAidForm = async (email) => {
    try {
      const response = await financialAidApi.lookup({ email });
      setFinancialAidData(response.data);
      setShowFinancialAidModal(true);
    } catch (error) {
      console.error("Error fetching financial aid details:", error);
      notificationCtx.show({
        message: "Failed to fetch financial aid details.",
        stat: "error",
      });
    }
  };

  const [activeCourses, setActiveCourses] = useState([]);
  const [courseDiscountRows, setCourseDiscountRows] = useState({});

  useEffect(() => {
    if (!data || Object.keys(courseDiscountRows).length > 0) {
      return;
    }

    const initialRows = {};

    const buildRows = (registration) => {
      const activeCourses = registration.courses.filter(
        (course) => course.status === "enrolled" || course.status === "pending",
      );

      if (activeCourses.length === 1) {
        return [
          {
            courseId: "all",
            discount: parseCourseDiscount(activeCourses[0].discount),
            percentage: parseCourseFee(activeCourses[0].fee)
              ? (parseCourseDiscount(activeCourses[0].discount) /
                  parseCourseFee(activeCourses[0].fee)) *
                100
              : 0,
            mode: "percentage",
            manualFee: "",
          },
        ];
      }

      return activeCourses.map((course) => ({
        courseId: course.id,
        discount: parseCourseDiscount(course.discount),
        percentage: parseCourseFee(course.fee)
          ? (parseCourseDiscount(course.discount) /
              parseCourseFee(course.fee)) *
            100
          : 0,
        mode: "percentage",
        manualFee: "",
      }));
    };

    data.forEach((registration) => {
      initialRows[registration.id] = buildRows(registration);
    });

    setCourseDiscountRows(initialRows);
  }, [data, courseDiscountRows]);

  useEffect(() => {
    if (!hasPermission) return;

    const fetchActiveCourses = async () => {
      try {
        const response = await coursesApi.list({
          exportMode: true,
          filters: { active: true },
        });
        setActiveCourses(normalizeListResponse(response.data).data);
      } catch (error) {
        console.error("Error fetching active courses:", error);
      }
    };

    fetchActiveCourses();
  }, [hasPermission]);

  const getCourseDiscountRows = (registrationId) => {
    const rows = courseDiscountRows[registrationId];
    return rows && rows.length > 0 ? rows : [{ courseId: "all", discount: 0 }];
  };

  const getActiveRegistrationCourses = (registration) =>
    registration.courses.filter((course) =>
      activeCourses.some((active) => active.id === course.id),
    );

  const getBaseFeeForRow = (registration, row) => {
    const activeCourses = getActiveRegistrationCourses(registration).filter(
      (course) => course.status === "enrolled" || course.status === "pending",
    );

    if (!activeCourses.length) {
      return 0;
    }

    if (row.courseId === "all") {
      return activeCourses.reduce(
        (total, course) => total + parseCourseFee(course.fee),
        0,
      );
    }

    const selectedCourse = activeCourses.find(
      (course) => String(course.id) === String(row.courseId),
    );

    return selectedCourse ? parseCourseFee(selectedCourse.fee) : 0;
  };

  const getMaxManualFeeForRow = (registration, row) => {
    const baseFee = getBaseFeeForRow(registration, row);
    return baseFee > 0 ? baseFee : 0;
  };

  const calculateDiscountFromManualFee = (
    registration,
    row,
    manualFeeValue,
  ) => {
    const baseFee = getBaseFeeForRow(registration, row);
    if (baseFee <= 0) {
      return 0;
    }

    const parsedFee = Number(manualFeeValue);
    if (!Number.isFinite(parsedFee)) {
      return 0;
    }

    const sanitizedFee = Math.min(Math.max(parsedFee, 0), baseFee);
    return baseFee - sanitizedFee;
  };

  const calculateDiscountFromPercentage = (registration, row, percentage) => {
    const baseFee = getBaseFeeForRow(registration, row);
    const parsedPercentage = Number(percentage);
    if (baseFee <= 0 || !Number.isFinite(parsedPercentage)) {
      return 0;
    }

    const sanitizedPercentage = Math.min(Math.max(parsedPercentage, 0), 100);
    return Math.round((baseFee * sanitizedPercentage) / 100);
  };

  const getPercentageForRow = (registration, row) => {
    const baseFee = getBaseFeeForRow(registration, row);
    if (baseFee <= 0) return 0;

    return (parseCourseDiscount(row.discount) / baseFee) * 100;
  };

  const getSelectedCourseIds = (registration, excludeRowIndex = -1) => {
    return new Set(
      getCourseDiscountRows(registration.id)
        .filter((row, index) => index !== excludeRowIndex)
        .filter((row) => row.courseId && row.courseId !== "all")
        .map((row) => Number(row.courseId)),
    );
  };

  const getAvailableCourseOptions = (registration, rowIndex) => {
    const selectedCourseIds = getSelectedCourseIds(registration, rowIndex);
    return getActiveRegistrationCourses(registration).filter(
      (course) => !selectedCourseIds.has(course.id),
    );
  };

  const hasAllCoursesSelected = (registrationId) =>
    getCourseDiscountRows(registrationId).some((row) => row.courseId === "all");

  const canAddCourseDiscountRow = (registration) => {
    if (hasAllCoursesSelected(registration.id)) {
      return false;
    }

    const selectedCount = getCourseDiscountRows(registration.id).filter(
      (row) => row.courseId && row.courseId !== "all",
    ).length;

    return getActiveRegistrationCourses(registration).length > selectedCount;
  };

  const setCourseDiscountRowsForRegistration = (registrationId, rows) => {
    setCourseDiscountRows((prev) => ({
      ...prev,
      [registrationId]: rows,
    }));
  };

  const handleCourseDiscountRowChange = (
    registrationId,
    rowIndex,
    field,
    value,
    registration,
  ) => {
    if (!canEdit) return;
    const rows = getCourseDiscountRows(registrationId);
    let updatedRows = rows.map((row, index) =>
      index === rowIndex ? { ...row, [field]: value } : row,
    );

    if (field === "courseId") {
      const existingRow = updatedRows[rowIndex];
      const previousRow = rows[rowIndex];
      const percentage =
        previousRow.percentage ??
        getPercentageForRow(registration, previousRow);
      const manualFeeValue = existingRow?.manualFee;
      const hasManualFee =
        manualFeeValue !== "" &&
        manualFeeValue !== null &&
        manualFeeValue !== undefined;

      if (value === "all") {
        const recalculatedDiscount = hasManualFee
          ? calculateDiscountFromManualFee(
              registration,
              existingRow,
              manualFeeValue,
            )
          : calculateDiscountFromPercentage(
              registration,
              existingRow,
              percentage,
            );

        updatedRows = [
          {
            courseId: "all",
            discount: recalculatedDiscount,
            percentage,
            mode: "percentage",
            manualFee: existingRow.manualFee || "",
          },
        ];
      } else if (hasManualFee) {
        updatedRows = rows.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                courseId: value,
                discount: calculateDiscountFromManualFee(
                  registration,
                  { ...row, courseId: value },
                  manualFeeValue,
                ),
                manualFee: manualFeeValue,
              }
            : row,
        );
      } else {
        updatedRows = rows.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                courseId: value,
                percentage,
                discount: calculateDiscountFromPercentage(
                  registration,
                  { ...row, courseId: value },
                  percentage,
                ),
              }
            : row,
        );
      }
    } else if (field === "percentage") {
      const percentage = Math.min(Math.max(Number(value) || 0, 0), 100);
      updatedRows = rows.map((row, index) =>
        index === rowIndex
          ? {
              ...row,
              percentage,
              discount: calculateDiscountFromPercentage(
                registration,
                row,
                percentage,
              ),
              manualFee: "",
            }
          : row,
      );
    }

    setCourseDiscountRowsForRegistration(registrationId, updatedRows);
  };

  const handleManualFeeChange = (registration, rowIndex, value) => {
    if (!canEdit) return;
    const rows = getCourseDiscountRows(registration.id);
    const maxFee = getMaxManualFeeForRow(registration, rows[rowIndex]);
    const numericValue = value === "" ? "" : Number(value);
    const sanitizedValue =
      value === "" ? "" : Math.min(Math.max(numericValue, 0), maxFee);

    const updatedRows = rows.map((row, index) =>
      index === rowIndex
        ? {
            ...row,
            manualFee: sanitizedValue,
            percentage: undefined,
            discount: calculateDiscountFromManualFee(
              registration,
              row,
              sanitizedValue,
            ),
          }
        : row,
    );

    setCourseDiscountRowsForRegistration(registration.id, updatedRows);
  };

  const addCourseDiscountRow = (registrationId) => {
    if (!canEdit) return;
    const rows = getCourseDiscountRows(registrationId);
    setCourseDiscountRowsForRegistration(registrationId, [
      ...rows,
      {
        courseId: "all",
        discount: 0,
        percentage: 0,
        mode: "percentage",
        manualFee: "",
      },
    ]);
  };

  const removeCourseDiscountRow = (registrationId, rowIndex) => {
    if (!canEdit) return;
    const rows = getCourseDiscountRows(registrationId);
    if (rows.length <= 1) {
      setCourseDiscountRowsForRegistration(registrationId, [
        {
          courseId: "all",
          discount: 0,
          percentage: 0,
          mode: "percentage",
          manualFee: "",
        },
      ]);
      return;
    }

    setCourseDiscountRowsForRegistration(
      registrationId,
      rows.filter((_, index) => index !== rowIndex),
    );
  };

  const getDiscountedFee = (registration) => {
    const rows = getCourseDiscountRows(registration.id);
    const allRow = rows.find((row) => row.courseId === "all");
    if (allRow) {
      const totalFee = registration.courses.reduce(
        (total, course) =>
          course.status === "enrolled" || course.status === "pending"
            ? total + parseCourseFee(course.fee)
            : total,
        0,
      );
      return Math.max(0, totalFee - parseCourseDiscount(allRow.discount));
    }

    const selectedDiscounts = new Map(
      rows
        .filter((row) => row.courseId && row.courseId !== "all")
        .map((row) => [
          Number(row.courseId),
          parseCourseDiscount(row.discount),
        ]),
    );

    return registration.courses.reduce((acc, curr) => {
      if (curr.status !== "enrolled" && curr.status !== "pending") {
        return acc;
      }

      const fee = parseCourseFee(curr.fee);
      const discount = selectedDiscounts.has(curr.id)
        ? selectedDiscounts.get(curr.id)
        : parseCourseDiscount(curr.discount);

      return acc + Math.max(0, Math.round(fee - discount));
    }, 0);
  };

  const saveCourseDiscounts = (registrationId) => {
    if (!canEdit) return;

    const confirmSave = async () => {
      await runMutation(async () => {
        const rows = getCourseDiscountRows(registrationId);
        const allRow = rows.find((row) => row.courseId === "all");
        const registration = data.find((item) => item.id === registrationId);
        const activeRegistrationCourses = registration
          ? registration.courses.filter(
              (course) =>
                course.status === "enrolled" || course.status === "pending",
            )
          : [];
        const payload = allRow
          ? (() => {
              const totalFee = activeRegistrationCourses.reduce(
                (total, course) => total + parseCourseFee(course.fee),
                0,
              );
              const totalDiscount = Math.min(
                parseCourseDiscount(allRow.discount),
                totalFee,
              );
              let remainingDiscount = totalDiscount;
              return activeRegistrationCourses.map((course, index) => {
                const isLastCourse =
                  index === activeRegistrationCourses.length - 1;
                const courseDiscount = isLastCourse
                  ? remainingDiscount
                  : Math.round(
                      (totalDiscount * parseCourseFee(course.fee)) / totalFee,
                    );
                remainingDiscount -= courseDiscount;
                return {
                  courseId: Number(course.id),
                  discount: courseDiscount,
                };
              });
            })()
          : rows
              .filter((row) => row.courseId)
              .map((row) => ({
                courseId: Number(row.courseId),
                discount: parseCourseDiscount(row.discount),
              }));

        if (payload.length === 0) {
          notificationCtx.show({
            message: "Select at least one course and discount before saving.",
            stat: "error",
          });
          return;
        }

        try {
          await registrationsApi.updateCourseDiscounts(registrationId, {
            courseDiscounts: payload,
          });

          notificationCtx.show({
            message: "Course discounts saved successfully!",
            stat: "success",
          });

          setdata((prev) =>
            prev.map((registration) => {
              if (registration.id !== registrationId) return registration;

              const updatedCourses = registration.courses.map((course) => {
                const updatedRow = payload.find(
                  (row) => row.courseId === course.id,
                );
                return updatedRow
                  ? { ...course, discount: updatedRow.discount }
                  : course;
              });

              return { ...registration, courses: updatedCourses };
            }),
          );
        } catch (error) {
          console.error("Error saving course discounts:", error);
          notificationCtx.show({
            message: "Failed to save course discounts.",
            stat: "error",
          });
        }
      });
    };

    notificationCtx.show({
      message: "Are you sure you want to save these course discounts?",
      stat: "warning",
      onConfirm: confirmSave,
    });
  };

  const RejectRequest = (id) => {
    if (!canEdit) return;

    const confirmReject = async () => {
      await runMutation(async () => {
        try {
          const field = "feemode";
          const value = "full";

          await registrationsApi.update(id, { field, value });
          setdata((prev) => prev.filter((r) => r.id !== id));
          notificationCtx.show({
            message: "Registration updated successfully!",
            stat: "success",
          });
        } catch (error) {
          console.error("Error updating registration:", error);
          notificationCtx.show({
            message: "Failed to update the registration.",
            stat: "error",
          });
        }
      });
    };

    notificationCtx.show({
      message:
        "Are you sure you want to reject this financial aid request? The student will be set to full fee mode.",
      stat: "warning",
      onConfirm: confirmReject,
    });
  };

  const ReturnForEdit = (email) => {
    if (!canEdit) return;

    const confirmReturn = async () => {
      await runMutation(async () => {
        try {
          const response = await financialAidApi.lookup({ email });
          await tempAidApi.create(response.data);
          await financialAidApi.deleteByEmail({ email });
          setdata((prev) => prev.filter((r) => r.email !== email));
          notificationCtx.show({
            message: "Financial aid details returned for edit successfully!",
            stat: "success",
          });
        } catch (error) {
          console.error("Error updating registration:", error);
          notificationCtx.show({
            message: "Failed to update the registration.",
            stat: "error",
          });
        }
      });
    };

    notificationCtx.show({
      message:
        "Are you sure you want to return this financial aid request for edit?",
      stat: "warning",
      onConfirm: confirmReturn,
    });
  };

  const [manualFeeEntry, setManualFeeEntry] = useState({});

  if (authLoading || permLoading || initialLoading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div className="mb-5">
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            minHeight: "0",
            height: "109vh",
            overflow: "hidden",
            padding: "10px 50px 0",
          }}
        >
          {/* ============================================================= */}
          {/* PAGE HEADER */}
          {/* ============================================================= */}
          <div
            className="mb-4"
            style={{
              backgroundColor: "#d5d1fd",
              borderRadius: "20px",
              padding: "24px",
            }}
          >
            {/* =========================================================== */}
            {/* HEADER */}
            {/* =========================================================== */}
            <div
              className="d-flex justify-content-between align-items-start"
              style={{
                gap: "20px",
                marginBottom: "22px",
              }}
            >
              <div>
                <h1
                  className="mb-1"
                  style={{
                    // fontSize: "28px",
                    fontWeight: "700",
                    color: "#302d45",
                  }}
                >
                  FINANCIAL AID REQUESTS
                </h1>

                <small
                  style={{
                    color: "#625d75",
                    fontSize: "14px",
                  }}
                >
                  Review financial aid requests and manage student fee
                  adjustments
                </small>
              </div>

              {data?.length > 0 && (
                <div
                  className="flex-shrink-0"
                  style={{
                    backgroundColor: "#f9e1be",
                    borderRadius: "12px",
                    padding: "9px 15px",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#3f3b4d",
                    whiteSpace: "nowrap",
                    marginTop: "4px",
                  }}
                >
                  {data.length} {data.length === 1 ? "Request" : "Requests"}
                </div>
              )}
            </div>

            {/* =========================================================== */}
            {/* FILTER / CONTROL BAR */}
            {/* =========================================================== */}
            <div className="row">
              <div className="col-md-4">
                <input
                  type="text"
                  placeholder="Search by Email..."
                  value={emailFilter}
                  onChange={handleEmailFilterChange}
                  className="form-control"
                  style={{
                    borderRadius: "10px",
                    border: "1px solid #d8d2ca",
                    padding: "10px 13px",
                    backgroundColor: "#ffffff",
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* ============================================================= */}
          {/* MAIN CONTENT */}
          {/* ============================================================= */}
          {data?.length === 0 ? (
            <div
              style={{
                backgroundColor: "#f9e1be",
                borderRadius: "20px",
                padding: "70px 30px",
                textAlign: "center",
              }}
            >
              <h4
                className="fw-bold mb-2"
                style={{
                  color: "#292631",
                }}
              >
                No financial aid requests available
              </h4>

              <small
                style={{
                  color: "#625d75",
                }}
              >
                There are currently no financial aid requests to review.
              </small>
            </div>
          ) : (
            <>
              {/* =========================================================== */}
              {/* TABLE CARD */}
              {/* =========================================================== */}
              {data ? (
                <>
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      borderRadius: "14px",
                      border: "1px solid #eeeaf8",
                      boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                    }}
                  >
                    {/* TABLE */}
                    <div
                      style={{
                        height: "100%",
                        overflow: "auto",
                      }}
                    >
                      <table
                        style={{
                          minWidth: "1100px",
                          width: "100%",
                          tableLayout: "auto",
                          background: "#fff",
                          borderCollapse: "separate",
                          borderSpacing: 0,
                        }}
                      >
                        <colgroup>
                          {columnKeys.map((key) => (
                            <col key={key} />
                          ))}
                        </colgroup>

                        <thead
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 5,
                          }}
                        >
                          <tr>
                            {columnKeys.map((key, index, arr) => (
                              <th
                                key={key}
                                style={{
                                  position: "relative",
                                  backgroundColor: "#d5d1fd",
                                  color: "#292631",
                                  fontWeight: "700",
                                  padding: "14px 12px",
                                  textAlign: "center",
                                  whiteSpace: "nowrap",
                                  letterSpacing: "0.4px",
                                  fontSize: "12px",
                                  borderBottom: "1px solid #ddd6ce",
                                }}
                              >
                                {key.toUpperCase()}

                                {index < arr.length - 1 && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      right: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: "4px",
                                      cursor: "col-resize",
                                    }}
                                  />
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>

                        <tbody>
                          {data.map((registration, index) => (
                            <tr
                              key={index}
                              style={{
                                borderBottom: "1px solid #eeeae5",
                              }}
                            >
                              {/* ================================================= */}
                              {/* STUDENT */}
                              {/* ================================================= */}
                              <td
                                style={{
                                  padding: "15px 14px",
                                  verticalAlign: "top",
                                  minWidth: "190px",
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    color: "#292631",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {registration.name}
                                </div>

                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#625d75",
                                    lineHeight: "1.6",
                                  }}
                                >
                                  {registration.email}
                                  <br />
                                  {registration.city}
                                  <br />
                                  {registration.phone}
                                </div>
                              </td>

                              {/* ================================================= */}
                              {/* COURSES */}
                              {/* ================================================= */}
                              <td
                                style={{
                                  padding: "15px 14px",
                                  verticalAlign: "top",
                                  minWidth: "220px",
                                  whiteSpace: "normal",
                                  lineHeight: "1.6",
                                }}
                              >
                                {registration.courses
                                  .filter(
                                    (course) =>
                                      course.status === "enrolled" ||
                                      course.status === "pending",
                                  )
                                  .map((course) => (
                                    <div
                                      key={course.id}
                                      style={{
                                        marginBottom: "4px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {course.title}
                                    </div>
                                  ))}
                              </td>

                              {/* ================================================= */}
                              {/* CURRENT FEE */}
                              {/* ================================================= */}
                              <td
                                style={{
                                  padding: "15px 14px",
                                  verticalAlign: "top",
                                  minWidth: "150px",
                                  textAlign: "center",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: "#625d75",
                                    lineHeight: "1.7",
                                  }}
                                >
                                  {registration.courses
                                    .filter(
                                      (course) =>
                                        course.status === "enrolled" ||
                                        course.status === "pending",
                                    )
                                    .map((course) => (
                                      <div key={course.id}>
                                        PKR{" "}
                                        {parseCourseFee(
                                          course.fee,
                                        ).toLocaleString()}
                                      </div>
                                    ))}
                                </div>

                                <div
                                  style={{
                                    marginTop: "8px",
                                    paddingTop: "8px",
                                    borderTop: "1px solid #eeeae5",
                                    fontWeight: "700",
                                    color: "#292631",
                                  }}
                                >
                                  PKR{" "}
                                  {registration.courses
                                    .reduce((acc, curr) => {
                                      if (
                                        curr.status === "enrolled" ||
                                        curr.status === "pending"
                                      ) {
                                        return acc + parseCourseFee(curr.fee);
                                      }

                                      return acc;
                                    }, 0)
                                    .toLocaleString()}
                                  <span
                                    style={{
                                      fontWeight: "500",
                                      fontSize: "11px",
                                      color: "#625d75",
                                    }}
                                  >
                                    {" "}
                                    /month
                                  </span>
                                </div>
                              </td>

                              {/* ================================================= */}
                              {/* ACTIONS */}
                              {/* ================================================= */}
                              <td
                                style={{
                                  padding: "15px 14px",
                                  verticalAlign: "top",
                                  minWidth: "250px",
                                }}
                              >
                                <div
                                  className="d-flex flex-column align-items-center gap-2"
                                  style={{
                                    minWidth: "150px",
                                  }}
                                >
                                  {/* VIEW - First Row */}
                                  <button
                                    className="btn btn-sm w-100"
                                    onClick={() =>
                                      viewFinancialAidForm(registration.email)
                                    }
                                    style={{
                                      backgroundColor: "#d5d1fd",
                                      borderColor: "#d5d1fd",
                                      color: "#000",
                                      fontWeight: "600",
                                      borderRadius: "9px",
                                      padding: "8px 14px",
                                    }}
                                  >
                                    VIEW
                                  </button>

                                  {/* RETURN + REJECT - Second Row */}
                                  {canEdit && (
                                    <div
                                      className="d-flex w-100"
                                      style={{
                                        gap: "8px",
                                      }}
                                    >
                                      <button
                                        className="btn btn-sm flex-fill"
                                        style={{
                                          backgroundColor: "#bff1db",
                                          borderColor: "#bff1db",
                                          color: "#000",
                                          fontWeight: "600",
                                          borderRadius: "9px",
                                          padding: "8px 10px",
                                        }}
                                        onClick={() =>
                                          ReturnForEdit(registration.email)
                                        }
                                        disabled={mutating}
                                      >
                                        {mutating ? "..." : "RETURN"}
                                      </button>

                                      <button
                                        onClick={() =>
                                          RejectRequest(registration.id)
                                        }
                                        disabled={mutating}
                                        className="btn btn-sm flex-fill"
                                        style={{
                                          backgroundColor: "#f2c4c4",
                                          borderColor: "#f2c4c4",
                                          color: "#000",
                                          fontWeight: "600",
                                          borderRadius: "9px",
                                          padding: "8px 10px",
                                        }}
                                      >
                                        {mutating ? "..." : "REJECT"}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>

                              {/* ================================================= */}
                              {/* DISCOUNT CONTROLS */}
                              {/* ================================================= */}
                              <td
                                style={{
                                  padding: "15px 14px",
                                  verticalAlign: "top",
                                  minWidth: "470px",
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor: "#f9e1be",
                                    borderRadius: "13px",
                                    padding: "12px",
                                  }}
                                >
                                  {getCourseDiscountRows(registration.id).map(
                                    (row, rowIndex) => (
                                      <div
                                        key={rowIndex}
                                        className="d-flex align-items-center gap-2 mb-2"
                                      >
                                        <select
                                          value={row.courseId}
                                          disabled={!canEdit}
                                          onChange={(e) =>
                                            handleCourseDiscountRowChange(
                                              registration.id,
                                              rowIndex,
                                              "courseId",
                                              e.target.value,
                                              registration,
                                            )
                                          }
                                          className="form-select form-select-sm"
                                          style={{
                                            minWidth: "165px",
                                            backgroundColor: "#ffffff",
                                            borderRadius: "8px",
                                          }}
                                        >
                                          <option value="all">
                                            All Courses
                                          </option>

                                          {getAvailableCourseOptions(
                                            registration,
                                            rowIndex,
                                          ).map((course) => (
                                            <option
                                              key={course.id}
                                              value={course.id}
                                            >
                                              {course.title}
                                            </option>
                                          ))}
                                        </select>

                                        {manualFeeEntry[registration.id] ? (
                                          <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            style={{
                                              minWidth: "190px",
                                              backgroundColor: "#ffffff",
                                              borderRadius: "8px",
                                            }}
                                            placeholder="Fee after discount"
                                            min="0"
                                            step="any"
                                            disabled={!canEdit}
                                            max={getMaxManualFeeForRow(
                                              registration,
                                              row,
                                            )}
                                            value={row.manualFee ?? ""}
                                            onChange={(e) =>
                                              handleManualFeeChange(
                                                registration,
                                                rowIndex,
                                                e.target.value,
                                              )
                                            }
                                          />
                                        ) : (
                                          <select
                                            value={String(
                                              row.percentage ??
                                                getPercentageForRow(
                                                  registration,
                                                  row,
                                                ),
                                            )}
                                            disabled={!canEdit}
                                            onChange={(e) =>
                                              handleCourseDiscountRowChange(
                                                registration.id,
                                                rowIndex,
                                                "percentage",
                                                e.target.value,
                                                registration,
                                              )
                                            }
                                            className="form-select form-select-sm"
                                            style={{
                                              width: "125px",
                                              backgroundColor: "#ffffff",
                                              borderRadius: "8px",
                                            }}
                                          >
                                            {presetDiscountOptions.map(
                                              (option) => (
                                                <option
                                                  key={option}
                                                  value={String(option)}
                                                >
                                                  {option}%
                                                </option>
                                              ),
                                            )}

                                            {!presetDiscountOptions.some(
                                              (option) =>
                                                String(option) ===
                                                String(
                                                  row.percentage ??
                                                    getPercentageForRow(
                                                      registration,
                                                      row,
                                                    ),
                                                ),
                                            ) && (
                                              <option
                                                value={String(
                                                  row.percentage ??
                                                    getPercentageForRow(
                                                      registration,
                                                      row,
                                                    ),
                                                )}
                                              >
                                                {Number(
                                                  row.percentage ??
                                                    getPercentageForRow(
                                                      registration,
                                                      row,
                                                    ),
                                                ).toFixed(2)}
                                                %
                                              </option>
                                            )}
                                          </select>
                                        )}

                                        {canEdit && rowIndex > 0 && (
                                          <button
                                            className="btn btn-sm"
                                            style={{
                                              backgroundColor: "#f2c4c4",
                                              borderColor: "#f2c4c4",
                                              borderRadius: "8px",
                                              fontWeight: "700",
                                              minWidth: "32px",
                                            }}
                                            onClick={() =>
                                              removeCourseDiscountRow(
                                                registration.id,
                                                rowIndex,
                                              )
                                            }
                                          >
                                            −
                                          </button>
                                        )}
                                      </div>
                                    ),
                                  )}

                                  {/* DISCOUNT ACTIONS */}
                                  {canEdit && (
                                  <div className="d-flex flex-wrap gap-2 mt-3">
                                    {canAddCourseDiscountRow(registration) && (
                                      <button
                                        className="btn btn-sm"
                                        style={{
                                          backgroundColor: "#d5d1fd",
                                          borderColor: "#d5d1fd",
                                          color: "#000",
                                          fontWeight: "600",
                                          borderRadius: "9px",
                                        }}
                                        onClick={() =>
                                          addCourseDiscountRow(registration.id)
                                        }
                                      >
                                        + ADD
                                      </button>
                                    )}

                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        backgroundColor: "#bff1db",
                                        borderColor: "#bff1db",
                                        color: "#000",
                                        fontWeight: "600",
                                        borderRadius: "9px",
                                      }}
                                      onClick={() =>
                                        saveCourseDiscounts(registration.id)
                                      }
                                      disabled={mutating}
                                    >
                                      {mutating ? "..." : "SAVE"}
                                    </button>

                                    <button
                                      className="btn btn-sm"
                                      style={{
                                        backgroundColor: "#f9e1be",
                                        borderColor: "#f9e1be",
                                        color: "#000",
                                        fontWeight: "600",
                                        borderRadius: "9px",
                                      }}
                                      onClick={() => {
                                        const isManualMode =
                                          manualFeeEntry[registration.id];

                                        notificationCtx.show({
                                          message: isManualMode ? (
                                            <>
                                              <div
                                                style={{
                                                  fontWeight: "bold",
                                                  fontSize: "18px",
                                                }}
                                              >
                                                You are about to switch back to
                                                Discount Percentage mode.
                                              </div>

                                              <br />

                                              <div
                                                style={{
                                                  fontSize: "16px",
                                                }}
                                              >
                                                The student's payable fee will
                                                be calculated automatically
                                                based on the selected discount
                                                percentage.
                                              </div>
                                            </>
                                          ) : (
                                            <>
                                              <div
                                                style={{
                                                  fontWeight: "bold",
                                                  fontSize: "18px",
                                                }}
                                              >
                                                You are about to switch to
                                                Manual Fee Entry mode.
                                              </div>

                                              <br />

                                              <div
                                                style={{
                                                  fontSize: "16px",
                                                }}
                                              >
                                                You will need to enter the final
                                                fee amount that the student
                                                should pay after discounts have
                                                been applied.
                                              </div>
                                            </>
                                          ),

                                          stat: "info",

                                          onConfirm: () => {
                                            setManualFeeEntry((prev) => ({
                                              ...prev,
                                              [registration.id]:
                                                !prev[registration.id],
                                            }));
                                          },
                                        });
                                      }}
                                    >
                                      {manualFeeEntry[registration.id]
                                        ? "SET DISCOUNT %"
                                        : "ENTER MANUAL FEE"}
                                    </button>
                                  </div>
                                  )}
                                </div>
                              </td>

                              {/* ================================================= */}
                              {/* DISCOUNTED FEE */}
                              {/* ================================================= */}
                              <td
                                style={{
                                  padding: "15px 14px",
                                  verticalAlign: "top",
                                  minWidth: "150px",
                                  textAlign: "center",
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor: "#bff1db",
                                    borderRadius: "12px",
                                    padding: "12px 10px",
                                    display: "inline-block",
                                    minWidth: "125px",
                                  }}
                                >
                                  <small
                                    style={{
                                      display: "block",
                                      color: "#3f5d4d",
                                      fontSize: "10px",
                                      fontWeight: "600",
                                      marginBottom: "3px",
                                    }}
                                  >
                                    PAYABLE FEE
                                  </small>

                                  <strong
                                    style={{
                                      fontSize: "15px",
                                      color: "#000",
                                    }}
                                  >
                                    PKR{" "}
                                    {getDiscountedFee(
                                      registration,
                                    ).toLocaleString()}
                                  </strong>

                                  <small
                                    style={{
                                      display: "block",
                                      color: "#3f5d4d",
                                      fontSize: "10px",
                                    }}
                                  >
                                    /month
                                  </small>
                                </div>
                              </td>

                              {/* ================================================= */}
                              {/* COMMENTS */}
                              {/* ================================================= */}
                              <td
                                style={{
                                  padding: "15px 14px",
                                  verticalAlign: "top",
                                  minWidth: "180px",
                                  width: "180px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "7px",
                                    width: "100%",
                                  }}
                                >
                                  <AppTooltip
                                    label={
                                      registration.comments || "No comments"
                                    }
                                    wrap
                                  >
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: "#625d75",
                                      maxWidth: "125px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {registration.comments || "-"}
                                  </span>
                                  </AppTooltip>

                                  <AppTooltip label="View Comment">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedComment(registration);
                                      setCommentValue(
                                        registration.comments || "",
                                      );
                                      setEditingComment(false);
                                    }}
                                    style={{
                                      width: "36px",
                                      height: "36px",
                                      padding: 0,
                                      border: "1px solid #e5e1ef",
                                      borderRadius: "7px",
                                      backgroundColor: "#fff",
                                      color: "#625d75",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <i
                                      className="fa-regular fa-comment"
                                      style={{ fontSize: "12px" }}
                                    />
                                  </button>
                                  </AppTooltip>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {selectedComment && (
                      <div
                        style={{
                          position: "fixed",
                          inset: 0,
                          backgroundColor: "rgba(48, 44, 64, 0.45)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1050,
                          padding: "20px",
                        }}
                        onClick={() => {
                          setSelectedComment(null);
                          setEditingComment(false);
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            maxWidth: "500px",
                            backgroundColor: "#fff",
                            borderRadius: "14px",
                            boxShadow: "0 12px 35px rgba(48, 44, 64, 0.18)",
                            overflow: "hidden",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Header */}
                          <div
                            style={{
                              padding: "16px 20px",
                              backgroundColor: "#d5d1fd",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "9px",
                              }}
                            >
                              <div
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "8px",
                                  backgroundColor: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#625d75",
                                }}
                              >
                                <i className="fa-regular fa-comment" />
                              </div>

                              <div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#302c40",
                                  }}
                                >
                                  Comment
                                </div>

                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#625d75",
                                    marginTop: "2px",
                                  }}
                                >
                                  {selectedComment.name || "Registration"}
                                </div>
                              </div>
                            </div>

                            <AppTooltip label="Close">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedComment(null);
                                setEditingComment(false);
                              }}
                              style={{
                                width: "30px",
                                height: "30px",
                                padding: 0,
                                border: "none",
                                borderRadius: "7px",
                                backgroundColor: "rgba(255,255,255,0.65)",
                                color: "#625d75",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                            >
                              <i className="fa-solid fa-xmark" />
                            </button>
                            </AppTooltip>
                          </div>

                          {/* Content */}
                          <div style={{ padding: "20px" }}>
                            {editingComment ? (
                              <textarea
                                value={commentValue}
                                onChange={(e) =>
                                  setCommentValue(e.target.value)
                                }
                                autoFocus
                                placeholder="Enter comment..."
                                style={{
                                  width: "100%",
                                  minHeight: "150px",
                                  resize: "vertical",
                                  padding: "11px 12px",
                                  border: "1px solid #dcd8e8",
                                  borderRadius: "8px",
                                  outline: "none",
                                  fontSize: "13px",
                                  lineHeight: "1.5",
                                  color: "#302c40",
                                  backgroundColor: "#fff",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  minHeight: "100px",
                                  maxHeight: "260px",
                                  overflowY: "auto",
                                  padding: "13px 14px",
                                  backgroundColor: "#faf9fc",
                                  border: "1px solid #e5e1ef",
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                  lineHeight: "1.6",
                                  color: "#625d75",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {selectedComment.comments ||
                                  "No comment has been added."}
                              </div>
                            )}
                          </div>

                          {/* Footer */}
                          <div
                            style={{
                              padding: "0 20px 18px",
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: "8px",
                            }}
                          >
                            {editingComment ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCommentValue(
                                      selectedComment.comments || "",
                                    );
                                    setEditingComment(false);
                                  }}
                                  style={{
                                    height: "34px",
                                    padding: "0 13px",
                                    border: "1px solid #e5e1ef",
                                    borderRadius: "7px",
                                    backgroundColor: "#fff",
                                    color: "#625d75",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                  }}
                                >
                                  Cancel
                                </button>

                                <button
                                  type="button"
                                  onClick={async () => {
                                    await handleCommentEdit(
                                      selectedComment.id,
                                      "comments",
                                      commentValue,
                                    );

                                    setSelectedComment((prev) => ({
                                      ...prev,
                                      comments: commentValue,
                                    }));

                                    setEditingComment(false);
                                  }}
                                  style={{
                                    height: "34px",
                                    padding: "0 14px",
                                    border: "1px solid #a8dfc5",
                                    borderRadius: "7px",
                                    backgroundColor: "#bff1db",
                                    color: "#355c4a",
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    cursor: "pointer",
                                  }}
                                >
                                  <i
                                    className="fa-solid fa-check"
                                    style={{ marginRight: "6px" }}
                                  />
                                  Save
                                </button>
                              </>
                            ) : (
                              canEdit && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setEditingComment(true)}
                                  style={{
                                    height: "34px",
                                    padding: "0 14px",
                                    border: "1px solid #e5e1ef",
                                    borderRadius: "7px",
                                    backgroundColor: "#fff",
                                    color: "#625d75",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                  }}
                                >
                                  <i
                                    className="fa-solid fa-pen"
                                    style={{ marginRight: "6px" }}
                                  />
                                  Edit
                                </button>
                              </>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <PaginationFooter
                    page={currentPage}
                    limit={limit}
                    total={total}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemLabel="financial aid requests"
                    disabled={refreshing}
                  />

                  {/* =========================================================== */}
                  {/* FINANCIAL AID DETAILS MODAL */}
                  {/* =========================================================== */}
                  {showFinancialAidModal && (
                    <div
                      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                      style={{
                        backgroundColor: "rgba(0,0,0,0.7)",
                        zIndex: 1055,
                        padding: "20px",
                      }}
                      onClick={() => setShowFinancialAidModal(false)}
                    >
                      <div
                        style={{
                          width: "100%",
                          maxWidth: "900px",
                          maxHeight: "90vh",
                          backgroundColor: "#ffffff",
                          borderRadius: "20px",
                          boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* MODAL HEADER */}
                        <div
                          className="d-flex align-items-center justify-content-between"
                          style={{
                            backgroundColor: "#d5d1fd",
                            padding: "19px 24px",
                            flexShrink: 0,
                          }}
                        >
                          <div>
                            <h3
                              className="fw-bold mb-1"
                              style={{
                                fontSize: "22px",
                              }}
                            >
                              FINANCIAL AID DETAILS
                            </h3>

                            <small
                              style={{
                                color: "#3f3b4d",
                              }}
                            >
                              Review the submitted financial aid information
                            </small>
                          </div>

                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setShowFinancialAidModal(false)}
                            aria-label="Close"
                          />
                        </div>

                        {/* MODAL BODY */}
                        <div
                          style={{
                            overflowY: "auto",
                            padding: "22px",
                            flex: "1 1 auto",
                            minHeight: 0,
                          }}
                        >
                          {financialAidData ? (
                            <div
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "15px",
                                padding: "18px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                              }}
                            >
                              <FinancialAidViewer
                                data={financialAidData}
                                columnKeys={tableKeys}
                                handleEdit={handleEdit}
                                handleSave={handleSave}
                                canEdit={canEdit}
                              />
                            </div>
                          ) : (
                            <div
                              className="text-center"
                              style={{
                                padding: "50px 20px",
                                color: "#625d75",
                              }}
                            >
                              <h6 className="fw-semibold">
                                Record not available
                              </h6>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Loader />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
